import { InputField } from "@/components/common/input-field";
import { useState } from "react";
import { SelectField } from "@/features/profile/SelectField";
import { DobField } from "./DobField";
import { Button } from "@/components/ui/button";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import type { Passenger } from "@/types/booking-history/bookinghistorydetail";
import {
  formatNameInput,
  passengerDetailsSchema,
  type PassengerDetailsForm,
} from "../schema/passenger.schema";
import { UpdatePassenger } from "@/services/bookinghistory.service";
import { useAuthStore } from "@/stores/auth.store";
import type {
  PassengerDetails,
  UpdatePassengerRequest,
} from "@/types/booking-history/updatepassenger";
import NationalityField from "@/components/common/NationalityField";
import { cn } from "@/lib/utils";

const FIELD_CLASS =
  "h-14 rounded-2xl border-border bg-card px-4 text-sm font-medium text-muted-foreground placeholder:text-border";

const GENDER_OPTIONS = ["Male", "Female"];
const isMalaysiaNationality = (value?: string) =>
  value === "Malaysia" || value === "MYS";

type PassengerDetailsState = {
  passengers: Passenger[];
  passengerIndex: number;
};

export function AddPassengerDetailsForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const paymentId = useParams().paymentId;

  const { passengers = [], passengerIndex = 0 } = (location.state ??
    {}) as Partial<PassengerDetailsState>;

  const passengerCount = passengers.length;
  const [current, setCurrent] = useState<number>(passengerIndex);

  const [passengerForms, setPassengerForms] = useState<PassengerDetailsForm[]>(
    () =>
      passengers.map((passenger) => {
        const ticketTypeCategoryName = passenger.ticketTypeCategoryName ?? "";
        const rawNationality = passenger.passengerIdentityNationality ?? "";
        const nationality =
          ticketTypeCategoryName === "Malaysian"
            ? "MYS"
            : ticketTypeCategoryName === "Foreigner" &&
                isMalaysiaNationality(rawNationality)
              ? ""
              : rawNationality === "Malaysia"
                ? "MYS"
                : rawNationality;

        return {
          paymentTicketId: passenger.paymentTicketId ?? "",
          ageFrom: passenger.ageFrom,
          ageTo: passenger.ageTo,
          firstName: passenger.passengerIdentityFirstName ?? "",
          lastName: passenger.passengerIdentityLastName ?? "",
          gender: passenger.passengerIdentityGender ?? "",
          nationality,
          dob: passenger.passengerIdentityDOB
            ? new Date(passenger.passengerIdentityDOB)
            : undefined,
          identityNumber: passenger.passengerIdentityNo ?? "",
        };
      }),
  );

  const [saving, setSaving] = useState(false);
  const [identityNumberError, setIdentityNumberError] = useState<string>("");

  const currentPassenger = passengers[current];
  const currentForm = passengerForms[current];
  const currentTicketCategoryName = currentPassenger?.ticketTypeCategoryName ?? "";
  const hasMalaysianTicketType = currentTicketCategoryName === "Malaysian";
  const hasForeignerTicketType = currentTicketCategoryName === "Foreigner";
  const normalizedCurrentNationality = hasMalaysianTicketType
    ? "MYS"
    : hasForeignerTicketType && isMalaysiaNationality(currentForm?.nationality)
      ? ""
      : currentForm?.nationality ?? "";

  const currentValidation = currentForm
    ? passengerDetailsSchema.safeParse({
        ...currentForm,
        nationality: normalizedCurrentNationality,
      })
    : undefined;
  const currentDobError =
    currentForm?.dob && currentValidation && !currentValidation.success
      ? currentValidation.error.issues.find((issue) => issue.path[0] === "dob")
          ?.message
      : undefined;

  const formatIdentityNumber = (value: string, nationality: string) => {
    const cleaned = value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();

    // Non-Malaysia → Passport only
    if (!isMalaysiaNationality(nationality)) {
      return cleaned.slice(0, 15);
    }

    // Malaysia → MYKAD OR Passport
    //
    // If user is typing letters, treat as passport
    if (/[A-Z]/.test(cleaned)) {
      return cleaned.slice(0, 15);
    }

    // Numeric input → MYKAD
    const digits = cleaned.slice(0, 12);

    if (digits.length <= 6) {
      return digits;
    }

    if (digits.length <= 8) {
      return `${digits.slice(0, 6)}-${digits.slice(6)}`;
    }

    return `${digits.slice(0, 6)}-${digits.slice(6, 8)}-${digits.slice(8)}`;
  };

  /* Update only current passenger */
  const updateCurrentPassenger = <K extends keyof PassengerDetailsForm>(
    field: K,
    value: PassengerDetailsForm[K],
  ) => {
    setPassengerForms((prev) =>
      prev.map((passenger, index) =>
        index === current
          ? {
              ...passenger,
              [field]: value,
            }
          : passenger,
      ),
    );
  };

  const validateIdentityNumber = (
    identityNumber: string,
    form: PassengerDetailsForm,
  ) => {
    // Jangan show error kalau kosong
    if (!identityNumber) {
      setIdentityNumberError("");
      return;
    }

    const result = passengerDetailsSchema.safeParse(form);

    if (!result.success) {
      const error = result.error.issues.find(
        (issue) => issue.path[0] === "identityNumber",
      );

      setIdentityNumberError(error?.message ?? "");
    } else {
      setIdentityNumberError("");
    }
  };

  /* Validate current passenger */
  const isCurrentPassengerValid =
    currentForm !== undefined && currentValidation?.success === true;

  /* Next passenger */
  const handleNext = () => {
    if (!isCurrentPassengerValid) {
      toast.error("Please complete all passenger details.");
      return;
    }

    setCurrent((prev) => Math.min(passengerCount - 1, prev + 1));
  };

  /* Previous passenger */
  const handlePrevious = () => {
    setCurrent((prev) => Math.max(0, prev - 1));
  };

  /*Save all passengers */
  const handleSave = async () => {
    // Validate ALL passengers before API call
    const validationResults = passengerForms.map((passenger) =>
      passengerDetailsSchema.safeParse(passenger),
    );

    const hasInvalidPassenger = validationResults.some(
      (result) => !result.success,
    );

    if (hasInvalidPassenger) {
      toast.error("Please complete all passenger details.");
      return;
    }

    try {
      setSaving(true);

      const updatepsg: PassengerDetails[] = passengerForms.map((passenger) => ({
        paymentTicketId: passenger.paymentTicketId,
        passengerIdentityFirstName: passenger.firstName,
        passengerIdentityLastName: passenger.lastName,
        passengerIdentityGender: passenger.gender,
        passengerIdentityNationality:
          passenger.nationality === "Malaysia" || passenger.nationality === "MYS"
            ? "MYS"
            : passenger.nationality,
        // Use UTC midnight of the selected local date to avoid the date shifting
        // back a day when toISOString() converts a local-midnight Date to UTC.
        passengerIdentityDOB: new Date(
          Date.UTC(
            passenger.dob!.getFullYear(),
            passenger.dob!.getMonth(),
            passenger.dob!.getDate(),
          ),
        ).toISOString(),
        passengerIdentityNo: passenger.identityNumber,
      }));

      const payload: UpdatePassengerRequest = {
        agentCompanySellerId:
          useAuthStore.getState().user?.agentCompanySellerId ?? "",
        paymentId: paymentId ?? "",
        passengers: updatepsg,
      };

      await UpdatePassenger(payload);

      toast.success("Passenger details saved successfully.");

      navigate(-1);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : "Failed to save passenger details.",
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="mt-8 flex justify-between items-end">
        <h2 className="text-lg font-semibold text-secondary-foreground md:text-base">
          {currentPassenger?.name}
        </h2>
        <span className="text-sm font-semibold text-heading md:text-base">
          {current + 1} of {passengers.length}
        </span>
      </div>

      <form>
        <div className="mt-2 rounded-2xl px-4 bg-card py-6 shadow-xl mb-8">
          <div className="flex flex-col gap-4 md:grid md:grid-cols-3">
            <InputField
              label="First Name"
              required
              placeholder="Enter Name"
              value={currentForm?.firstName ?? ""}
              maxLength={50}
              onChange={(e) => {
                updateCurrentPassenger(
                  "firstName",
                  formatNameInput(e.target.value),
                );
              }}
              className={FIELD_CLASS}
            />

            <InputField
              label="Last Name"
              required
              placeholder="Enter Name"
              value={currentForm?.lastName ?? ""}
              maxLength={50}
              onChange={(e) => {
                updateCurrentPassenger(
                  "lastName",
                  formatNameInput(e.target.value),
                );
              }}
              className={FIELD_CLASS}
            />

            <SelectField
              label="Gender"
              required
              placeholder="Select Gender"
              value={currentForm?.gender ?? ""}
              options={GENDER_OPTIONS}
              className={FIELD_CLASS}
              onSelect={(value) => updateCurrentPassenger("gender", value)}
            />

            <NationalityField
              value={normalizedCurrentNationality}
              disabled={hasMalaysianTicketType}
              allowMalaysia={!hasForeignerTicketType}
              onSelect={(value) => {
                if (
                  hasForeignerTicketType &&
                  (value === "Malaysia" || value === "MYS")
                ) {
                  toast.error(
                    "Foreigner passengers cannot choose Malaysia as nationality.",
                  );
                  return;
                }

                updateCurrentPassenger("nationality", value);

                // Clear MYKAD / Passport bila nationality dipilih semula
                updateCurrentPassenger("identityNumber", "");
                setIdentityNumberError("");
              }}
            />

            <DobField
              label="Date of Birth"
              required
              placeholder="Select Date of Birth"
              value={currentForm?.dob}
              className={FIELD_CLASS}
              error={currentDobError}
              onSelect={(value) => {
                updateCurrentPassenger("dob", value);

                const updatedForm = {
                  ...currentForm,
                  dob: value,
                };

                validateIdentityNumber(
                  currentForm?.identityNumber ?? "",
                  updatedForm,
                );
              }}
            />

            <InputField
              label="MYKAD/Passport No."
              required
              disabled={!currentForm?.nationality}
              placeholder={
                !currentForm?.nationality
                  ? "Enter MYKAD or Passport No."
                  : isMalaysiaNationality(currentForm.nationality)
                    ? "Enter MYKAD or Passport No."
                    : "Enter Passport No."
              }
              value={currentForm?.identityNumber ?? ""}
              onChange={(e) => {
                const formattedValue = formatIdentityNumber(
                  e.target.value,
                  currentForm?.nationality ?? "",
                );

                const updatedForm = {
                  ...currentForm,
                  identityNumber: formattedValue,
                };

                updateCurrentPassenger("identityNumber", formattedValue);

                validateIdentityNumber(formattedValue, updatedForm);
              }}
              error={identityNumberError}
              className={cn(
                FIELD_CLASS,
                identityNumberError && "border-destructive",
              )}
            />
          </div>
          <div className="mt-6 hidden md:flex justify-end gap-4">
            <Button
              type="button"
              onClick={handlePrevious}
              disabled={current === 0}
              className="w-2xs cursor-pointer"
            >
              Previous
            </Button>

            {current === passengerCount - 1 ? (
              <Button
                type="button"
                onClick={handleSave}
                disabled={!isCurrentPassengerValid || saving}
                className="w-2xs cursor-pointer"
              >
                {saving ? "Saving..." : "Save"}
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleNext}
                disabled={!isCurrentPassengerValid}
                className="w-2xs cursor-pointer"
              >
                Next
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:hidden">
          <Button
            type="button"
            onClick={handlePrevious}
            disabled={current === 0}
            className="cursor-pointer"
          >
            Previous
          </Button>

          {current === passengerCount - 1 ? (
            <Button
              type="button"
              onClick={handleSave}
              disabled={!isCurrentPassengerValid || saving}
              className="cursor-pointer"
            >
              {saving ? "Saving..." : "Save"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleNext}
              disabled={!isCurrentPassengerValid}
              className="cursor-pointer"
            >
              Next
            </Button>
          )}
        </div>
      </form>
    </>
  );
}
